/**
 * The kit's keyboard (docs/spec/07 §4): every tool and command has a key. Arrows are left to the
 * app's menu actions when they are bound to them, so holding one repeats.
 */

import type { KeyInput } from '../../ui/scene';
import { paint } from '../model/grid';
import { KIT_ELEMENTS, paletteIndex } from '../model/palette';
import { TOOLS } from '../model/shapes';
import type { TextField } from '../model/text';
import { TEXT_MAX, typeChar } from '../model/text';
import { scrollBy } from '../model/view';
import type { Command, KitApi } from './api';
import { FORM_ITEMS } from './formItems';
import { VIEW } from './layout';

export type ArrowDir = 'up' | 'down' | 'left' | 'right';

const MODIFIERS = new Set([
  'ShiftLeft', 'ShiftRight', 'ControlLeft', 'ControlRight', 'AltLeft', 'AltRight', 'MetaLeft', 'MetaRight',
]);
const ARROWS: Readonly<Record<string, ArrowDir>> = {
  ArrowUp: 'up', ArrowDown: 'down', ArrowLeft: 'left', ArrowRight: 'right',
};
const PAGES: Readonly<Record<string, readonly [number, number]>> = {
  PageUp: [0, -1], PageDown: [0, 1], Home: [-1, 0], End: [1, 0],
};

/** Plain letter keys for commands (tools come from `TOOLS`). */
const LETTERS: Readonly<Record<string, Command>> = {
  KeyZ: 'zoom', KeyG: 'grid', KeyV: 'preview', KeyT: 'test', KeyC: 'share', KeyN: 'new', KeyO: 'library',
};

function textKey(k: KitApi, field: TextField, key: KeyInput): void {
  const st = k.st;
  if (key.code === 'Enter' || key.code === 'NumpadEnter' || key.code === 'Escape' || key.code === 'Tab') {
    k.editing = null;
    st.history.seal();
    k.sound('select');
    return;
  }
  const text = st.doc[field];
  if (key.code === 'Backspace') {
    if (text.length > 0) st.edit({ ...st.doc, [field]: text.slice(0, -1) }, field);
    k.sound('type');
    return;
  }
  if (key.ctrl || key.key.length !== 1) return;
  const next = typeChar(field, text, key.key);
  if (next === null) {
    k.sound('error');
    if (text.length >= TEXT_MAX[field]) st.say(`THE ${field.toUpperCase()} IS FULL: ${TEXT_MAX[field]} CHARACTERS`);
    return;
  }
  st.edit({ ...st.doc, [field]: next }, field);
  k.sound('type');
}

export function stepElement(k: KitApi, d: number): void {
  const i = paletteIndex(k.element);
  const next = KIT_ELEMENTS[(i + d + KIT_ELEMENTS.length) % KIT_ELEMENTS.length];
  if (next) k.selectElement(next.ch);
}

/** Keys that work wherever the focus is. True when handled. */
function globalKey(k: KitApi, key: KeyInput): boolean {
  const c = key.code;
  const command: Command | null =
    key.ctrl && c === 'KeyZ' ? (key.shift ? 'redo' : 'undo')
    : key.ctrl && c === 'KeyY' ? 'redo'
    : key.ctrl && c === 'KeyS' ? 'save'
    : c === 'F1' || key.key === '?' ? 'help'
    : null;
  if (command) {
    k.run(command);
    return true;
  }
  if (c !== 'Tab') return false;
  const n = FORM_ITEMS.length;
  if (k.focus === 'cave') {
    k.focus = 'form';
    k.formIndex = key.shift ? n - 1 : 0;
  } else k.formIndex = (k.formIndex + (key.shift ? n - 1 : 1)) % n;
  k.st.history.seal();
  k.sound('move');
  return true;
}

function formKey(k: KitApi, key: KeyInput): boolean {
  const item = FORM_ITEMS[k.formIndex];
  const c = key.code;
  if (c === 'Escape') {
    k.focus = 'cave';
    k.sound('back');
  } else if (c === 'Enter' || c === 'NumpadEnter' || c === 'Space') item?.activate?.(k);
  else if (c === 'Delete' || c === 'Backspace') item?.clear?.(k);
  else return false;
  return true;
}

function caveKey(k: KitApi, key: KeyInput): boolean {
  const c = key.code;
  const tool = key.ctrl ? undefined : TOOLS.find((t) => t.code === c);
  const command = key.ctrl ? undefined : LETTERS[c];
  const page = PAGES[c];
  if (c === 'Space' || c === 'Enter' || c === 'NumpadEnter') k.painter.key(k.cursor);
  else if (c === 'Delete') k.st.edit({ ...k.st.doc, grid: paint(k.st.doc.grid, [k.cursor], '.') });
  else if (c === 'Escape' || c === 'Backspace') {
    if (k.painter.stroke) {
      k.painter.cancel();
      k.sound('back');
    } else if (c === 'Escape') k.run('back');
  } else if (tool) k.selectTool(tool.id);
  else if (c === 'BracketLeft' || c === 'Comma') stepElement(k, -1);
  else if (c === 'BracketRight' || c === 'Period') stepElement(k, 1);
  else if (page) k.view = scrollBy(k.view, k.st.doc.grid, VIEW, page[0] * (VIEW.w - 32), page[1] * (VIEW.h - 32));
  else if (command) k.run(command);
  else return false;
  return true;
}

/**
 * A key for the kit with no dialog open. True when it is handled here; false lets the app turn it
 * into a menu action (arrows bound to directions, so they repeat while held).
 */
export function kitKey(
  k: KitApi,
  key: KeyInput,
  repeats: (code: string) => boolean,
  arrow: (d: ArrowDir) => void,
): boolean {
  if (k.editing) {
    // Ctrl+Z, Ctrl+S and friends still work while typing.
    if (!(key.ctrl && globalKey(k, key))) textKey(k, k.editing, key);
    return true;
  }
  if (MODIFIERS.has(key.code)) return true;
  const dir = ARROWS[key.code];
  if (dir) {
    if (repeats(key.code)) return false;
    arrow(dir);
    return true;
  }
  if (globalKey(k, key)) return true;
  if (k.focus === 'form' && formKey(k, key)) return true;
  if (caveKey(k, key)) return true;
  // Anything else is swallowed, so no stray menu action follows, except the browser's own keys.
  return !(key.ctrl || /^F\d+$/.test(key.code));
}
